import { useMemo } from 'react';
import { useModalContext } from '@/contexts/ModalContext';
import { useUser } from '@/stores/authStore';
import { PoolListItem, PoolStatus } from '@/types/pool';
import { DraftPoolListItem } from '@/types/pool/create';
import { LoanTypeLabel, formatCurrency, formatPercentage } from '@/utils/misc';
import { calculateLTV } from '@/utils/poolCalculations';
import { POOL_TYPE_DISPLAY, isRefinancing } from '@/utils/poolDisplay';
import { getAcceptedCounterOfferInfo, hasCounterOffer } from '@/utils/poolStatus';
import { PoolAmountDisplay } from './PoolAmountDisplay';
import { PoolCardContainer } from './PoolCardContainer';
import { PoolCardHeader } from './PoolCardHeader';
import PoolCardInfo from './PoolCardInfo';

type InvestorPoolCardProps =
  | {
      pool: PoolListItem;
      draft?: undefined;
    }
  | {
      pool?: undefined;
      draft: DraftPoolListItem;
    };

type CardInfo = {
  id: number;
  status: PoolStatus;
  loanType: string;
  poolType: string;
  displayAmount: string | null;
  displayRoi: string;
  displayTermMonths: string;
  propertyValue: string;
  displayLtv: string;
  hasCounterOffer: boolean;
  isFavorite: boolean;
};

function formatOrFallback(
  value: string | number | null | undefined,
  format: (v: string | number) => string,
  fallback: string
): string {
  return value != null && value !== '' ? format(value) : fallback;
}

export default function PoolCard({ pool, draft }: InvestorPoolCardProps) {
  const user = useUser();
  const { openPoolModal } = useModalContext();

  const cardInfo = useMemo<CardInfo>(() => {
    const source = pool
      ? (() => {
          const counter = getAcceptedCounterOfferInfo(pool.offers);
          const amount = counter?.amount ?? pool.loan_terms.amount;
          const roiRate = counter?.roi_rate ?? pool.loan_terms.roi_rate;
          const termMonths = counter?.term_months ?? pool.loan_terms.term_months;
          const propertyValueNum = pool.property?.details.property_value;
          const existingBalances = pool.property?.existing_loans?.map((l) => l.remaining_balance) ?? [];
          return {
            id: pool.id,
            status: pool.status,
            amount,
            roiRate,
            termMonths,
            loanType: LoanTypeLabel[pool.loan_terms.loan_type],
            poolType: POOL_TYPE_DISPLAY[pool.pool_type],
            propertyValueNum,
            existingBalances,
            isRefinancing: isRefinancing(pool.pool_type),
            offers: pool.offers,
            isFavorite: pool.is_favorite,
          };
        })()
      : {
          id: draft.id,
          status: PoolStatus.DRAFT as PoolStatus,
          amount: draft.data.loan_terms.amount ?? null,
          roiRate: draft.data.loan_terms.roi_rate,
          termMonths: draft.data.loan_terms.term_months ?? '--',
          loanType: draft.data.loan_terms.loan_type ?? '--',
          poolType: draft.data.pool_type ?? '--',
          propertyValueNum: draft.data.property_detail?.property_value,
          existingBalances: draft.data.existing_loans?.map((l) => l?.remaining_balance ?? '') ?? [],
          isRefinancing: draft.data.pool_type ? isRefinancing(draft.data.pool_type) : false,
          offers: null,
          isFavorite: false,
        };

    const displayAmount = source.amount != null ? source.amount : null;
    const displayRoi = formatOrFallback(source.roiRate, formatPercentage, '--');
    const displayTermMonths = String(source.termMonths ?? '--');
    const propertyValue = formatOrFallback(source.propertyValueNum, formatCurrency, '--');
    const displayLtv =
      displayAmount != null && source.propertyValueNum != null
        ? formatPercentage(
            calculateLTV(
              parseFloat(displayAmount),
              source.propertyValueNum,
              source.existingBalances,
              source.isRefinancing
            )
          )
        : '--';

    return {
      id: source.id,
      status: source.status,
      loanType: source.loanType,
      poolType: source.poolType,
      displayAmount,
      displayRoi,
      displayTermMonths,
      propertyValue,
      displayLtv,
      hasCounterOffer: source.offers != null ? hasCounterOffer(source.offers) : false,
      isFavorite: source.isFavorite,
    };
  }, [pool, draft]);

  const args = pool
    ? { type: 'link' as const }
    : {
        type: 'button' as const,
        onClick: () => {
          openPoolModal({ draft });
        },
      };

  return (
    <PoolCardContainer poolId={cardInfo.id} hoverEffect="shadow" className="p-6" borderColor="#113D7B" {...args}>
      <div className="flex flex-col gap-4">
        <PoolCardHeader
          poolId={cardInfo.id}
          status={cardInfo.status}
          counterOffer={cardInfo.hasCounterOffer}
          className="items-start"
          user={user}
          isFavorite={cardInfo.isFavorite}
        />
        <PoolAmountDisplay amount={cardInfo.displayAmount} amountSize="sm" showLabel label="Requested amount" />

        <div className="grid grid-cols-2 grid-rows-3 gap-3 mt-5">
          <PoolCardInfo title="Pool Type" value={cardInfo.poolType} />
          <PoolCardInfo title="Interest Rate" value={cardInfo.displayRoi} />
          <PoolCardInfo title="Pool Program" value={cardInfo.loanType} />
          <PoolCardInfo title="Term" value={`${cardInfo.displayTermMonths} months`} />
          <PoolCardInfo title="Property Value" value={cardInfo.propertyValue} />
          <PoolCardInfo title="Estimated LTV" value={cardInfo.displayLtv} />
        </div>
      </div>
    </PoolCardContainer>
  );
}
